import { memo, useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";
import moment from "moment";
import { getDecryptLocalStorage } from "../../utils/global_functions";
import { LOCAL_STORAGE_KEYS } from "../../utils/global_variables";

const AnalysisLine = ({ lineData, showNormInd, showCloseInd, indice }) => {
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const normIndSeriesRef = useRef(null);
    const closeIndSeriesRef = useRef(null);
    const tooltipRef = useRef(null);
    const strActiveTheme = getDecryptLocalStorage(LOCAL_STORAGE_KEYS.THEME);

    useEffect(() => {
        if (!lineData || lineData.length === 0) return;

        const formattedData = lineData.map(item => ({
            time: moment(item.date).unix(),
            normInd: item.normPort,
            closeInd: item.closeInd,
        }));

        const chart = createChart(chartContainerRef.current, {
            width: chartContainerRef.current.offsetWidth,
            height: 220,
            layout: {
                backgroundColor: "#ffffff",
                textColor: "#000000",
            },
            grid: {
                vertLines: { color: "#e1e1e1" },
                horzLines: { color: "#e1e1e1" },
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false,
                tickMarkFormatter: time =>
                    moment.unix(time).format("MMM YY").toUpperCase(),
            },
            rightPriceScale: {
                visible: true,
                borderVisible: true,
                autoScale: true,
                scaleMargins: {
                    top: 0.3,
                    bottom: 0.3,
                },
            },
            leftPriceScale: {
                visible: false,
            },
        });

        const normIndSeries = chart.addLineSeries({
            color: "#4188fd",
            lineWidth: 2,
            visible: showNormInd,
        });

        const closeIndSeries = chart.addLineSeries({
            color: "#ffb13b", 
            lineWidth: 2,
            visible: showCloseInd,
        });

        normIndSeries.setData(
            formattedData.map(item => ({
                time: item.time,
                value: item.normInd,
            }))
        );

        closeIndSeries.setData(
            formattedData.map(item => ({
                time: item.time,
                value: item.closeInd,
            }))
        );

        chart.timeScale().fitContent();

        chartRef.current = chart;
        normIndSeriesRef.current = normIndSeries;
        closeIndSeriesRef.current = closeIndSeries;

        chart.subscribeCrosshairMove((param) => {
            if (!tooltipRef.current || !param.time || !param.point) {
                tooltipRef.current.style.display = "none";
                return;
            }

            const normInd = normIndSeriesRef.current;
            const closeInd = closeIndSeriesRef.current;
            const pointNorm = param.seriesData.get(normInd);
            const pointClose = param.seriesData.get(closeInd);

            const showNormTooltip =
                pointNorm && showNormInd &&
                Math.abs(param.point.y - normInd.priceToCoordinate(pointNorm.value)) <= 5;

            const showCloseTooltip =
                pointClose && showCloseInd &&
                Math.abs(param.point.y - closeInd.priceToCoordinate(pointClose.value)) <= 5;

            if (showNormTooltip || showCloseTooltip) {
                const chartBounds = chartContainerRef.current.getBoundingClientRect();
                const tooltipWidth = tooltipRef.current.offsetWidth;
                const tooltipHeight = tooltipRef.current.offsetHeight;

                const tooltipX = Math.min(
                    Math.max(param.point.x - tooltipWidth / 2, 0),
                    chartBounds.width - tooltipWidth
                );
                const tooltipY = Math.min(
                    Math.max(param.point.y - tooltipHeight - 10, 0),
                    chartBounds.height - tooltipHeight
                );

                tooltipRef.current.style.left = `${tooltipX}px`;
                tooltipRef.current.style.top = `${tooltipY}px`;
                tooltipRef.current.style.display = "block";

                const formattedDate = moment.unix(param.time).format("MMM DD, YYYY");

                tooltipRef.current.innerHTML = `
                    ${showNormTooltip ? `<div style="color:#4188fd; text-transform:uppercase">Portfolio: ${pointNorm.value}</div>` : ""}
                    ${showCloseTooltip ? `<div style="color:#ffb13b;">${indice}: ${pointClose.value}</div>` : ""}
                    <div class="font-medium font-size1 color-text">${formattedDate}</div>
                `;
            } else {
                tooltipRef.current.style.display = "none";
            }
        });

        const handleResize = () => {
            chart.resize(chartContainerRef.current.offsetWidth, 220);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            chart.remove();
            window.removeEventListener("resize", handleResize);
        };
    }, [lineData, showNormInd, showCloseInd, indice, strActiveTheme]);

    useEffect(() => {
        if (normIndSeriesRef.current) {
            normIndSeriesRef.current.applyOptions({ visible: showNormInd });
        }
    }, [showNormInd]);

    useEffect(() => {
        if (closeIndSeriesRef.current) {
            closeIndSeriesRef.current.applyOptions({ visible: showCloseInd });
        }
    }, [showCloseInd]);

    return (
        <div className='position-relative w-100'>
            <div ref={chartContainerRef}></div>
            <div className='benchmark-tooltip' ref={tooltipRef}></div>
        </div>
    );
};

export default memo(AnalysisLine);
